import { AccountSettingsTabs, CreditRoutes, GroupTabs, MainRoutes, MemberTabs, ProjectTabs, QueryParams, SettingsTabs, TeamTabs, UsersTabs } from "@router/route-params";
import { UtilsTypes } from "@stellar/utils";
import { buildForbiddenRouteQueryString } from "@utils/route-utils";
// eslint-disable-next-line no-restricted-imports -- this is the only place that this is needed
import { generatePath, useParams } from "react-router-dom";

/** List of query parameters that should be preserved when navigating between pages */
export const PRESERVED_QUERY_PARAMS = [QueryParams.enableAlpha, QueryParams.enableBeta, QueryParams.enableDev, QueryParams.allowTranslationOverride, QueryParams.settingsOriginUrl];
/**
 * Use throughout app instead of plain `useParams` to get type-safety
 */
export function useAppParams() {
  return useParams();
}

/** Name of different paths in the app  */
export let EAppRoutes = /*#__PURE__*/function (EAppRoutes) {
  EAppRoutes["WorkspaceSelectionRoute"] = "WorkspaceSelectionRoute";
  EAppRoutes["AccountSettingsRoute"] = "AccountSettingsRoute";
  EAppRoutes["ProjectsRoute"] = "ProjectsRoute";
  EAppRoutes["ProjectDetailsRoute"] = "ProjectDetailsRoute";
  EAppRoutes["MembersRoute"] = "MembersRoute";
  EAppRoutes["GroupsRoute"] = "GroupsRoute";
  EAppRoutes["GroupsDetailsRoute"] = "GroupsDetailsRoute";
  EAppRoutes["MemberProfileRoute"] = "MemberProfileRoute";
  EAppRoutes["ForbiddenRoute"] = "ForbiddenRoute";
  EAppRoutes["TeamRoute"] = "TeamRoute";
  EAppRoutes["TeamOverviewRoute"] = "TeamOverviewRoute";
  EAppRoutes["SettingRoute"] = "SettingRoute";
  EAppRoutes["CreditRoute"] = "CreditRoute";
  EAppRoutes["CreditPurchasedRoute"] = "CreditPurchasedRoute";
  EAppRoutes["subscriptionExpired"] = "subscriptionExpired";
  return EAppRoutes;
}({});

/** List of routes available in the app */
export const AppRoutes = {
  /** The route of workspace selection */
  [EAppRoutes.WorkspaceSelectionRoute]: MainRoutes.root,
  /** The route of account settings selection */
  [EAppRoutes.AccountSettingsRoute]: `${MainRoutes.root}${MainRoutes.accountSettings}/:accountSettingsTab`,
  /** The route of projects with dynamic segments */
  [EAppRoutes.ProjectsRoute]: `${MainRoutes.app}/${MainRoutes.projects}/:projectStatus`,
  /** The route of project details with dynamic segments */
  [EAppRoutes.ProjectDetailsRoute]: `${MainRoutes.app}/${MainRoutes.projects}/:projectId/:projectTabs`,
  /** The route of company members */
  [EAppRoutes.MembersRoute]: `${MainRoutes.app}/${MainRoutes.members}/${UsersTabs.members}`,
  /** The route of company groups */
  [EAppRoutes.GroupsRoute]: `${MainRoutes.app}/${MainRoutes.groups}`,
  /** The route of group details with dynamic segments */
  [EAppRoutes.GroupsDetailsRoute]: `${MainRoutes.app}/${MainRoutes.groups}/:groupId/:groupTabs`,
  /** The route of member profile page */
  [EAppRoutes.MemberProfileRoute]: `${MainRoutes.app}/${MainRoutes.members}/${UsersTabs.members}/:memberId/:memberTabs`,
  /** The forbidden route */
  [EAppRoutes.ForbiddenRoute]: `${MainRoutes.app}/${MainRoutes.forbidden}`,
  /** The route of teams */
  [EAppRoutes.TeamRoute]: `${MainRoutes.app}/${MainRoutes.members}/${UsersTabs.teams}`,
  /** The route of team overview */
  [EAppRoutes.TeamOverviewRoute]: `${MainRoutes.app}/${MainRoutes.members}/${UsersTabs.teams}/:teamId/:teamTabs`,
  /** The route of the setting page */
  [EAppRoutes.SettingRoute]: `${MainRoutes.app}/${MainRoutes.settings}/:settingsTab`,
  /** The route of the add credits page */
  [EAppRoutes.CreditRoute]: `${MainRoutes.app}/${MainRoutes.credits}`,
  /** The route of the add credits page */
  [EAppRoutes.CreditPurchasedRoute]: `${MainRoutes.app}/${MainRoutes.credits}/${CreditRoutes.purchaseSuccess}`,
  /** The route of the subscription expired page */
  [EAppRoutes.subscriptionExpired]: `${MainRoutes.app}/${MainRoutes.subscriptionExpired}`
};

/**
 * Generates the projects route. The default tab is active if is not provided
 */
export function generateProjectsRoute(companyId, projectStatus = UtilsTypes.ProjectArchivingState.active) {
  return generatePath(AppRoutes.ProjectsRoute, {
    companyId,
    projectStatus
  });
}

/**
 * Generates the subscription route.
 */
export function generateSettingRoute(companyId, settingsTab = SettingsTabs.subscription) {
  return generatePath(AppRoutes.SettingRoute, {
    companyId,
    settingsTab
  });
}

/**
 * Generates the project details route. The default tab is Overview if it is not provided
 */
export function generateProjectDetailsRoute(companyId, projectId, projectTabs = ProjectTabs.overview) {
  return generatePath(AppRoutes.ProjectDetailsRoute, {
    companyId,
    projectId,
    projectTabs
  });
}

/**
 * Generates the group details route. The default tab is Managers if it is not provided
 */
export function generateGroupsRoute(companyId) {
  return generatePath(AppRoutes.GroupsRoute, {
    companyId
  });
}

/**
 * Generates the group details route. The default tab is Managers if it is not provided
 */
export function generateGroupDetailsRoute(companyId, groupId, groupTabs = GroupTabs.managers) {
  return generatePath(AppRoutes.GroupsDetailsRoute, {
    companyId,
    groupId,
    groupTabs
  });
}

/** Generates the route of the workspace members */
export function generateMembersRoute(companyId) {
  return generatePath(AppRoutes.MembersRoute, {
    companyId
  });
}

/** Generates the member overview route */
export function generateMemberProfileRoute(companyId, memberId) {
  return generatePath(AppRoutes.MemberProfileRoute, {
    companyId,
    memberId,
    memberTabs: MemberTabs.overview
  });
}

/**
 * Generates the path to the forbidden page route, including the
 * missing project roles as query parameters.
 */
export function generateForbiddenPageRoute({
  companyId,
  requiredCompanyRoles,
  requiredPermissions,
  requiredCompanySubscriptions,
  userId
}) {
  const path = generatePath(AppRoutes.ForbiddenRoute, {
    companyId
  });
  const query = buildForbiddenRouteQueryString({
    requiredCompanyRoles,
    requiredPermissions,
    requiredCompanySubscriptions,
    userId
  });
  return path + query;
}

/**
 * Generates the teams route.
 */
export function generateTeamsRoute(companyId) {
  return generatePath(AppRoutes.TeamRoute, {
    companyId
  });
}

/**
 * Generates the team details route. The default tab is overview if it is not provided
 */
export function generateTeamDetailsRoute(companyId, teamId, teamTabs = TeamTabs.overview) {
  return generatePath(AppRoutes.TeamOverviewRoute, {
    companyId,
    teamId,
    teamTabs
  });
}

/** Generates the account setting route. The default tab is security if it is not provided */
export function generateAccountSettingsRoute(accountSettingsTab = AccountSettingsTabs.security) {
  return generatePath(AppRoutes.AccountSettingsRoute, {
    accountSettingsTab
  });
}

/** Generates the credits route */
export function generateCreditsRoute(companyId) {
  return generatePath(AppRoutes.CreditRoute, {
    companyId
  });
}

/** Generates the credits full route with origin */
export function generateCreditsFullRoute(companyId) {
  const path = generatePath(AppRoutes.CreditRoute, {
    companyId
  });
  return window.location.origin + path;
}

/** Generates the credits purchase redirect route */
export function generateCreditsPurchaseRedirectFullRoute(companyId) {
  const path = generatePath(AppRoutes.CreditPurchasedRoute, {
    companyId
  });
  return window.location.origin + path;
}

/** Generates the subscription expired redirect route */
export function generateSubscriptionExpiredRoute(companyId) {
  return generatePath(AppRoutes.subscriptionExpired, {
    companyId
  });
}

/**
 * Extracts and returns preserved query parameters from the current URL.
 * Preserved parameters are defined in the `PRESERVED_QUERY_PARAMS` array.
 */
export function getPreservedQueryParams(location) {
  const searchParams = new URLSearchParams(location);
  const preservedParams = new URLSearchParams();
  PRESERVED_QUERY_PARAMS.forEach(param => {
    const value = searchParams.get(param);
    if (value !== null) {
      preservedParams.set(param, value);
    }
  });
  const preservedString = preservedParams.toString();
  return preservedString ? `?${preservedString}` : "";
}